import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { ElMessage, ElMessageBox } from 'element-plus'
import router from '@/router'
import storage, { StorageType } from '@/utils/storage'
import { baseClient } from '@/apollo-client'
import * as gql from '@/graphql'
import webSocket from '@/mqtt'

dayjs.extend(utc)

const RENEW_INTERVAL = 60000
// 距过期不足这个时长时弹出闲置提示
const WARN_AHEAD = 60000

let requestTimer: any = null
let compareTimer: any = null
// 当前受守护的登录会话，用于识别缓存里的会话是否已经被换掉
let guardedSessionId = ''
// 服务端返回的会话过期时间戳，0 表示还没拿到
let expireAt = 0
// 闲置提示弹出后暂停自动续期，等用户点「继续」再恢复
let renewable = true
// 失效弹窗只允许弹一次
let loggedOut = false
// 距上次成功续期后用户是否有过操作
let userActive = true

const readSessionId = (): string =>
  storage.rcGetItem(StorageType.local, 'sessionId') || ''

/**
 * 服务端按 UTC 口径下发 expireTime。字符串若不带时区标识，dayjs 会按浏览器本地时区解析，
 * 在 UTC+8 下会把过期时间提前 8 小时，续期成功反而立刻被判定成超时。
 * 因此本地解析结果不在未来时再按 UTC 解析一次；两者都不成立视为无效，保留上一次的过期时间。
 */
const parseExpireTime = (raw: any): number | null => {
  if (!raw) return null
  const now = Date.now()
  const local = dayjs(raw)
  if (local.isValid() && local.valueOf() > now) return local.valueOf()
  const asUtc = dayjs.utc(raw)
  if (asUtc.isValid() && asUtc.valueOf() > now) return asUtc.valueOf()
  return null
}

const onUserActive = () => {
  userActive = renewable
}

const clearTimers = () => {
  if (requestTimer) {
    clearInterval(requestTimer)
    requestTimer = null
  }
  if (compareTimer) {
    clearInterval(compareTimer)
    compareTimer = null
  }
}

const stop = () => {
  clearTimers()
  guardedSessionId = ''
  window.removeEventListener('mousemove', onUserActive)
  window.removeEventListener('keydown', onUserActive)
  window.removeEventListener('click', onUserActive)
}

const renew = (): Promise<boolean> =>
  baseClient
    .mutate({ mutation: gql.extendSession })
    .then((res: any) => {
      const extendSession = res?.data?.extendSession
      if (!extendSession?.ok) return false
      userActive = false
      const next = parseExpireTime(extendSession?.data?.expireTime)
      if (next) {
        expireAt = next
      }
      return true
    })
    .catch(() => false)

/**
 * 会话已消失（退出登录 / 被互踢 / 服务端 545）或者已经换成了另一次登录，
 * 都必须立刻收手，否则会把刚登录成功的新会话一起判定为失效。
 */
const isSessionAlive = (): boolean => {
  const sessionId = readSessionId()
  if (!sessionId || sessionId !== guardedSessionId) {
    stop()
    return false
  }
  return true
}

const forceLogout = () => {
  loggedOut = true
  stop()
  ElMessageBox.close()
  webSocket.close()
  storage.rcRemoveItem(StorageType.local, 'sessionId')
  storage.rcRemoveItem(StorageType.session, 'unlock')
  storage.rcRemoveItem(StorageType.session, 'reminder')
  ElMessageBox.alert('', '系统页面已失效, 你已退出页面', {
    confirmButtonText: '我知道了',
    showClose: false,
    customClass: 'message-info-out',
    center: true,
    type: 'warning',
    callback: () => {
      router.push('/login')
      ElMessage({
        message: '退出登录成功！',
        type: 'success',
        customClass: 'custom-success'
      })
    }
  })
}

const renewTick = () => {
  if (!isSessionAlive()) return
  if (!renewable) return
  if (!userActive) return
  renew()
}

const expireTick = () => {
  if (!isSessionAlive()) return
  if (!expireAt) return
  const rest = expireAt - Date.now()
  if (rest <= WARN_AHEAD && rest > 0) {
    if (!renewable) return
    renewable = false
    ElMessageBox.confirm(
      '您已闲置超过29分钟, 系统将会在1分钟后自动退出, 如您想继续使用本系统, 请点击"继续"按钮',
      '提示',
      {
        confirmButtonText: '继续',
        cancelButtonText: '取消',
        center: true,
        customClass: 'message-info-change',
        showClose: false,
        closeOnClickModal: false,
        type: 'warning'
      }
    )
      .then(() => {
        renew().then(ok => {
          if (ok) {
            renewable = true
          }
        })
      })
      .catch(() => ({}))
    return
  }
  if (rest <= 0 && !loggedOut) {
    forceLogout()
  }
}

/**
 * Layout 是 6 个顶层路由记录共用的组件，切换菜单分组会让它卸载重挂。
 * 续期状态必须挂在这个单例上：同一会话内重复调用保持原有计时，
 * 否则频繁切菜单会不断重置计时器，续期请求永远发不出去。
 */
const start = () => {
  const sessionId = readSessionId()
  if (!sessionId) return
  if (requestTimer && guardedSessionId === sessionId) return
  stop()
  guardedSessionId = sessionId
  expireAt = 0
  renewable = true
  loggedOut = false
  userActive = true
  window.addEventListener('mousemove', onUserActive)
  window.addEventListener('keydown', onUserActive)
  window.addEventListener('click', onUserActive)
  requestTimer = setInterval(renewTick, RENEW_INTERVAL)
  compareTimer = setInterval(expireTick, 1000)
}

export default { start, stop }
